import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Menu, X, User, LogOut, LayoutDashboard, ChevronDown, Phone, Mail, Package } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { getCartCount } = useCart();
  const { user, isAdmin, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const cartCount = getCartCount();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Left side nav links (main bar)
  const leftLinks = [
    { name: 'Home', path: '/' },
    { 
      name: 'About', 
      path: '/team',
      subLinks: [
        { name: 'About Us', path: '/team' },
        { name: 'Mission & Vision', path: '/mission' },
        { name: 'Gallery', path: '/gallery' }
      ]
    },
    { name: 'Products', path: '/products' },
    { name: 'Exports', path: '/exports' },
  ];

  // Right side nav links (main bar) — Track Order moved to utility bar
  const rightLinks = [
    { name: 'Become Distributor', path: '/become-dealer' },
    { name: 'Contact Us', path: '/contact' },
  ];

  // All links for mobile menu
  const allLinks = [
    ...leftLinks,
    ...rightLinks,
    { 
      name: 'Track Order', 
      path: '/your-orders',
      subLinks: [
        { name: 'Your Orders', path: '/your-orders' },
        { name: 'Track Shipment', path: '/track-shipment' }
      ]
    },
  ];

  const renderNavLink = (link) => {
    if (link.subLinks) {
      return (
        <div key={link.path} className="relative group">
          <button className="text-gray-700 hover:text-red-700 font-semibold transition-colors duration-200 flex items-center px-3 py-2 rounded-lg hover:bg-red-50" style={{ fontSize: '17px' }}>
            {link.name}
            <ChevronDown className="ml-1 h-3.5 w-3.5 opacity-50 group-hover:opacity-100 transition-opacity" />
          </button>
          <div className="invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200 absolute top-full left-0 mt-1 w-52 bg-white rounded-xl shadow-lg py-2 z-50 border border-gray-100">
            {link.subLinks.map(subLink => (
              <Link
                key={subLink.path}
                to={subLink.path}
                className="block px-4 py-2.5 text-gray-600 hover:bg-red-50 hover:text-red-700 transition-colors duration-200"
                style={{ fontSize: '14px' }}
              >
                {subLink.name}
              </Link>
            ))}
          </div>
        </div>
      );
    }
    return (
      <Link
        key={link.path}
        to={link.path}
        className="text-gray-700 hover:text-red-700 font-semibold transition-colors duration-200 px-3 py-2 rounded-lg hover:bg-red-50"
        style={{ fontSize: '17px' }}
      >
        {link.name}
      </Link>
    );
  };

  return (
    <header className="sticky top-0 z-50 bg-white shadow-sm">

      {/* ============ TOP UTILITY BAR ============ */}
      <div className="bg-red-800 text-white" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-9">
            
            {/* Left — Contact info */}
            <div className="hidden sm:flex items-center space-x-5 text-xs text-red-100">
              <a href="tel:07312431576" className="flex items-center space-x-1.5 hover:text-white transition-colors">
                <Phone className="h-3 w-3" />
                <span>0731-2431576</span>
              </a>
              <a href="mailto:samarthsurana@arihantmilkproducts.com" className="flex items-center space-x-1.5 hover:text-white transition-colors">
                <Mail className="h-3 w-3" />
                <span>samarthsurana@arihantmilkproducts.com</span>
              </a>
            </div>

            {/* Right — Track Order, Cart, Login */}
            <div className="flex items-center space-x-4 text-xs ml-auto">
              
              {/* Track Order dropdown */}
              <div className="relative group">
                <button className="flex items-center space-x-1.5 text-red-100 hover:text-white transition-colors py-2">
                  <Package className="h-3 w-3" />
                  <span>Track Order</span>
                  <ChevronDown className="h-3 w-3 opacity-60" />
                </button>
                <div className="invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200 absolute top-full right-0 mt-0 w-44 bg-white rounded-lg shadow-lg py-1.5 z-50 border border-gray-100">
                  <Link to="/your-orders" className="block px-3 py-2 text-sm text-gray-600 hover:bg-red-50 hover:text-red-700">Your Orders</Link>
                  <Link to="/track-shipment" className="block px-3 py-2 text-sm text-gray-600 hover:bg-red-50 hover:text-red-700">Track Shipment</Link>
                </div>
              </div>

              <span className="w-px h-3.5 bg-red-600"></span>

              {/* Cart */}
              <Link to="/cart" className="flex items-center space-x-1.5 text-red-100 hover:text-white transition-colors relative">
                <ShoppingCart className="h-3 w-3" />
                <span>Cart</span>
                {cartCount > 0 && (
                  <span className="absolute -top-1.5 -left-1.5 bg-yellow-400 text-red-900 text-[9px] rounded-full h-3.5 w-3.5 flex items-center justify-center font-bold">
                    {cartCount}
                  </span>
                )}
              </Link>

              <span className="w-px h-3.5 bg-red-600"></span>

              {/* Auth */}
              {isAuthenticated ? (
                <div className="flex items-center space-x-3">
                  {isAdmin && (
                    <Link to="/admin" className="flex items-center space-x-1 text-red-100 hover:text-white transition-colors">
                      <LayoutDashboard className="h-3 w-3" />
                      <span>Admin</span>
                    </Link>
                  )}
                  <span className="flex items-center space-x-1 text-red-100">
                    <User className="h-3 w-3" />
                    <span className="max-w-[70px] truncate">{user?.name}</span>
                  </span>
                  <button onClick={handleLogout} className="flex items-center space-x-1 text-red-100 hover:text-white transition-colors">
                    <LogOut className="h-3 w-3" />
                    <span>Logout</span>
                  </button>
                </div>
              ) : (
                <Link to="/login" className="bg-white/20 hover:bg-white/30 text-white px-3.5 py-1 rounded transition-colors font-medium text-xs">
                  Login
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ============ MAIN NAVBAR ============ */}
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        <div className="flex justify-between items-center" style={{ height: '82px' }}>

          {/* Left Navigation */}
          <div className="hidden lg:flex items-center space-x-1 flex-1 justify-end">
            {leftLinks.map(link => renderNavLink(link))}
          </div>

          {/* Center Logo — BIGGER */}
          <Link to="/" className="flex items-center flex-shrink-0 mx-6 lg:mx-10">
            <img 
              src="https://customer-assets.emergentagent.com/job_selected-repo/artifacts/x30z79ye_IMG_1165.PNG" 
              alt="Arihant Milk Products" 
              className="w-auto object-contain"
              style={{ height: '72px', background: 'transparent' }}
            />
          </Link>

          {/* Right Navigation */}
          <div className="hidden lg:flex items-center space-x-1 flex-1">
            {rightLinks.map(link => renderNavLink(link))}
          </div>

          {/* Mobile: Cart + Menu Button */}
          <div className="flex items-center space-x-2 lg:hidden">
            <Link to="/cart" className="relative">
              <Button variant="ghost" size="icon" className="relative">
                <ShoppingCart className="h-5 w-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-rose-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-semibold">
                    {cartCount}
                  </span>
                )}
              </Button>
            </Link>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-gray-600 hover:text-gray-900"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-100 bg-white">
            <div className="flex flex-col space-y-1">
              {allLinks.map(link => {
                if (link.subLinks) {
                  return (
                    <div key={link.path}>
                      <div className="text-gray-900 font-semibold text-sm py-2.5 px-3">{link.name}</div>
                      <div className="pl-6 space-y-1">
                        {link.subLinks.map(subLink => (
                          <Link
                            key={subLink.path}
                            to={subLink.path}
                            onClick={() => setMobileMenuOpen(false)}
                            className="block text-gray-500 hover:text-red-700 text-sm font-medium py-2 px-3 rounded-lg hover:bg-red-50"
                          >
                            {subLink.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  );
                }
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-gray-700 hover:text-red-700 text-sm font-medium py-2.5 px-3 rounded-lg hover:bg-red-50"
                  >
                    {link.name}
                  </Link>
                );
              })}

              <div className="border-t border-gray-100 pt-3 mt-2">
                {isAuthenticated ? (
                  <>
                    {isAdmin && (
                      <Link
                        to="/admin"
                        onClick={() => setMobileMenuOpen(false)}
                        className="block text-gray-700 hover:text-red-700 text-sm font-medium py-2.5 px-3 rounded-lg hover:bg-red-50"
                      >
                        Admin Dashboard
                      </Link>
                    )}
                    <button
                      onClick={() => {
                        handleLogout();
                        setMobileMenuOpen(false);
                      }}
                      className="w-full text-left text-gray-700 hover:text-red-700 text-sm font-medium py-2.5 px-3 rounded-lg hover:bg-red-50"
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block text-red-700 font-semibold text-sm py-2.5 px-3"
                  >
                    Login
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* ============ BOTTOM ACCENT BAR ============ */}
      <div className="h-1" style={{ background: 'linear-gradient(90deg, #F57C00, #B71C1C, #F57C00)' }}></div>
    </header>
  );
};

export default Header;
